// frontend/src/components/public/blocks/TestimonialsBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Avatar } from '@mui/material';
import { FormatQuote as QuoteIcon } from '@mui/icons-material';

interface TestimonialsBlockProps {
  data: {
    title?: string;
    subtitle?: string;
    testimonials: Array<{
      artist: string;
      organization?: string;
      quote: string;
      image?: string;
    }>;
    layout: 'grid' | 'carousel' | 'list';
    showCategories: boolean;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const TestimonialsBlock: React.FC<TestimonialsBlockProps> = ({ data, theme }) => {
  const { title, subtitle, testimonials = [], layout = 'grid' } = data;
  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  if (testimonials.length === 0) {
    return null;
  }

  // Grid Layout
  if (layout === 'grid') {
    return (
      <Box
       id="testimonials"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        {/* Section Header */}
        {(title || subtitle) && (
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {title && (
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: primaryColor,
                  mb: 2
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: '#666666',
                  fontWeight: 400
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  bgcolor: 'transparent',
                  border: `2px solid ${accentColor}`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${accentColor}44`
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <QuoteIcon
                    sx={{
                      fontSize: '3rem',
                      color: accentColor,
                      opacity: 0.5,
                      mb: 2
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.05rem',
                      lineHeight: 1.8,
                      color: '#333333',
                      fontStyle: 'italic',
                      mb: 3
                    }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {testimonial.image && (
                      <Avatar
                        src={testimonial.image}
                        sx={{ width: 56, height: 56 }}
                      />
                    )}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: primaryColor
                        }}
                      >
                        {testimonial.artist}
                      </Typography>
                      {testimonial.organization && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            color: '#666666'
                          }}
                        >
                          {testimonial.organization}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // List Layout
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '900px',
        mx: 'auto'
      }}
    >
      {/* Section Header */}
      {(title || subtitle) && (
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          {title && (
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: primaryColor,
                mb: 2
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: '#666666',
                fontWeight: 400
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {testimonials.map((testimonial, index) => (
        <Box
          key={index}
          sx={{
            mb: 5,
            pb: 5,
            borderBottom: index < testimonials.length - 1 ? `1px solid ${accentColor}33` : 'none'
          }}
        >
          <QuoteIcon
            sx={{
              fontSize: '2.5rem',
              color: accentColor,
              opacity: 0.5,
              mb: 2
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: '#333333',
              fontStyle: 'italic',
              mb: 3
            }}
          >
            "{testimonial.quote}"
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {testimonial.image && (
              <Avatar
                src={testimonial.image}
                sx={{ width: 64, height: 64 }}
              />
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: primaryColor
                }}
              >
                {testimonial.artist}
              </Typography>
              {testimonial.organization && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.95rem',
                    color: '#666666'
                  }}
                >
                  {testimonial.organization}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TestimonialsBlock;